import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Link } from 'react-router-dom';
import { useConnection } from 'wagmi';

import AirplaneIcon from '@/app/assets/svg/airplane.svg?react';
import { useUserPointsActivity } from '@/app/components/ReferralPageComponents/hooks/useUserPointsActivity';
import ReferralEmptyState from '@/app/components/ReferralPageComponents/ReferralEmptyState';
import { formatActivityDate } from '@/app/components/ReferralPageComponents/referralFormatters';
import { commonRoutes } from '@/app/providers/router/routes';
import { formatPoints } from '@/app/shared/lib/formatters';
import { Button } from '@/app/shared/ui/Button';
import PaginatedList from '@/app/shared/ui/PaginatedList';
import { Skeleton } from '@/app/shared/ui/Skeleton';

const ActivityList = () => {
  const { address } = useConnection();
  const { rows, isLoading } = useUserPointsActivity(address);

  if (!address) {
    return (
      <div className='mt-2.5 md:mt-10'>
        <div className='flex min-h-76 flex-col items-center justify-center gap-5 rounded-md bg-elevated-card-2 p-4'>
          <div className='flex flex-col items-center gap-1.5'>
            <span className='leading-5.5 font-medium text-brand-1100'>No activity yet</span>
            <p className='max-w-md text-center text-sm text-gray-400'>
              Connect your wallet to view your points activity.
            </p>
          </div>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <Button
                variant='ghost'
                onClick={openConnectModal}
                className='w-fit rounded-md bg-action-button px-4 py-2 text-sm leading-5 font-medium text-white hover:bg-action-button/90'
              >
                Connect Wallet
              </Button>
            )}
          </ConnectButton.Custom>
        </div>
      </div>
    );
  }

  if (!isLoading && !rows.length) {
    return (
      <div className='mt-2.5 md:mt-10'>
        <ReferralEmptyState
          title='No activity yet'
          description='Your points activity will appear here once you start earning points.'
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='mt-2.5 flex w-full flex-col gap-1.5 md:mt-10'>
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton.Line key={index} loading className='h-14' />
        ))}
      </div>
    );
  }

  return (
    <div className='mt-2.5 flex w-full flex-col gap-1.5 md:mt-10'>
      <PaginatedList
        itemsPerPage={5}
        className='grid min-h-70 grid-cols-[auto_auto_minmax(0,1fr)_auto] content-start gap-x-5 gap-y-1.5'
      >
        {rows.map((item, index) => (
          <div
            key={`${item.campaignKey}-${item.earnedDate}-${index}`}
            className='col-span-full grid h-14 grid-cols-subgrid items-center rounded-md bg-elevated-card-2 p-4'
          >
            <span className='leading-6 font-medium whitespace-nowrap text-modal-step-success tabular-nums'>
              +{formatPoints(item.points)} Points
            </span>

            <Link
              to={commonRoutes.REFERRAL}
              className='flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-feedback-green-100/12 text-feedback-green-100'
              aria-label='Invitee Boost'
            >
              <AirplaneIcon className='h-4 w-4' />
            </Link>

            <span className='min-w-0 truncate leading-6 text-gray-200'>{item.activityName}</span>
            <span className='justify-self-end leading-6 whitespace-nowrap text-gray-200'>
              {formatActivityDate(item.earnedDate)}
            </span>
          </div>
        ))}
      </PaginatedList>
    </div>
  );
};

export default ActivityList;
